import {
  BeginMembershipPaymentSubscriptionRequestDto,
  beginMaintenancePlanPaymentSubscriptionDtoSchema,
  guidSchema,
  nextGuid,
  PaymentMethod,
  PaymentMethodBillingInfoDto,
  PaymentMethodBillingInfoDtoSchema,
  PaymentMethodCardMetadata,
} from '@breezy/shared'
import { useCallback, useRef, useState } from 'react'
import { z } from 'zod'

export type RetainedPaymentMethod = {
  readonly cardMetadata?: PaymentMethodCardMetadata
  readonly externalPaymentMethodId: string
  readonly paymentMethod: PaymentMethod.CARD | PaymentMethod.ACH
  readonly paymentMethodAdditionalInfo: string
  readonly paymentMethodRecordGuid?: string
}

export type PaymentMethodRecordFinalization = {
  readonly billingInfo: PaymentMethodBillingInfoDto
  readonly cardMetadata?: PaymentMethodCardMetadata
}

type MaintenancePlanSubscriptionAttempt = {
  readonly activationRequest?: BeginMembershipPaymentSubscriptionRequestDto
  readonly maintenancePlanGuid: string
  readonly paymentMethodRecordFinalization?: PaymentMethodRecordFinalization
  readonly paymentSubscriptionGuid: string
  readonly retainedPaymentMethod?: RetainedPaymentMethod
  readonly version: 1
}

type AttemptState = {
  readonly attempt: MaintenancePlanSubscriptionAttempt
  readonly recoveryError?: Error
}

const cardMetadataSchema = z.object({
  lastFourDigits: z.string().optional(),
  expirationYear: z.string().optional(),
  expirationMonth: z.string().optional(),
  cardBrand: z.string().optional(),
  cardType: z.enum(['CREDIT', 'DEBIT', 'PREPAID', 'UNKNOWN']).optional(),
})

const retainedPaymentMethodSchema = z.object({
  cardMetadata: cardMetadataSchema.optional(),
  externalPaymentMethodId: z.string().min(1),
  paymentMethod: z.union([z.literal(PaymentMethod.CARD), z.literal(PaymentMethod.ACH)]),
  paymentMethodAdditionalInfo: z.string(),
  paymentMethodRecordGuid: guidSchema.optional(),
})

const paymentMethodRecordFinalizationSchema = z.object({
  billingInfo: PaymentMethodBillingInfoDtoSchema,
  cardMetadata: cardMetadataSchema.optional(),
})

const attemptSchema = z
  .object({
    activationRequest: beginMaintenancePlanPaymentSubscriptionDtoSchema.optional(),
    maintenancePlanGuid: guidSchema,
    paymentMethodRecordFinalization: paymentMethodRecordFinalizationSchema.optional(),
    paymentSubscriptionGuid: guidSchema,
    retainedPaymentMethod: retainedPaymentMethodSchema.optional(),
    version: z.literal(1),
  })
  .superRefine((attempt, context) => {
    const hasRequest = !!attempt.activationRequest
    const hasPaymentMethod = !!attempt.retainedPaymentMethod
    if (hasRequest !== hasPaymentMethod) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'A retained operation must be complete' })
      return
    }
    if (!attempt.activationRequest || !attempt.retainedPaymentMethod) return
    if (
      attempt.activationRequest.maintenancePlanGuid !== attempt.maintenancePlanGuid ||
      attempt.activationRequest.paymentSubscriptionGuid !== attempt.paymentSubscriptionGuid ||
      attempt.activationRequest.paymentMethodId !== attempt.retainedPaymentMethod.externalPaymentMethodId ||
      attempt.activationRequest.paymentMethod !== attempt.retainedPaymentMethod.paymentMethod ||
      attempt.activationRequest.paymentMethodAdditionalInfo !==
        attempt.retainedPaymentMethod.paymentMethodAdditionalInfo ||
      attempt.activationRequest.paymentMethodRecordGuid !== attempt.retainedPaymentMethod.paymentMethodRecordGuid
    ) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Retained operation identities do not match' })
    }
    const needsPaymentMethodFinalization = !attempt.retainedPaymentMethod.paymentMethodRecordGuid
    if (needsPaymentMethodFinalization && !attempt.paymentMethodRecordFinalization) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A newly tokenized payment method must retain finalization details',
      })
    }
    if (!needsPaymentMethodFinalization && attempt.paymentMethodRecordFinalization) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A saved payment method must not retain finalization details',
      })
    }
    if (needsPaymentMethodFinalization && !attempt.activationRequest.merchantId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A newly tokenized payment method must retain its Tilled account provenance',
      })
    }
  })

const storageKey = (maintenancePlanGuid: string) =>
  `breezy:maintenance-plan-subscription-attempt:${maintenancePlanGuid}`

const newAttempt = (maintenancePlanGuid: string): MaintenancePlanSubscriptionAttempt => ({
  maintenancePlanGuid,
  paymentSubscriptionGuid: nextGuid(),
  version: 1,
})

const toError = (error: unknown, message: string): Error =>
  error instanceof Error ? error : new Error(message, { cause: error })

const initializeAttempt = (maintenancePlanGuid: string): AttemptState => {
  const freshAttempt = newAttempt(maintenancePlanGuid)
  if (typeof window === 'undefined') return { attempt: freshAttempt }
  try {
    const raw = window.sessionStorage.getItem(storageKey(maintenancePlanGuid))
    if (raw) {
      const parsed = attemptSchema.safeParse(JSON.parse(raw))
      if (!parsed.success || parsed.data.maintenancePlanGuid !== maintenancePlanGuid) {
        return {
          attempt: freshAttempt,
          recoveryError: new Error('The saved subscription activation could not be safely recovered'),
        }
      }
      return { attempt: parsed.data }
    }
    window.sessionStorage.setItem(storageKey(maintenancePlanGuid), JSON.stringify(freshAttempt))
    return { attempt: freshAttempt }
  } catch (error) {
    return {
      attempt: freshAttempt,
      recoveryError: toError(error, 'Subscription activation recovery storage is unavailable'),
    }
  }
}

export const useMaintenancePlanSubscriptionAttempt = (maintenancePlanGuid: string) => {
  const [state, setState] = useState<AttemptState>(() => initializeAttempt(maintenancePlanGuid))
  const stateRef = useRef(state)

  const retainOperation = useCallback(
    (
      paymentMethod: RetainedPaymentMethod,
      activationRequest: BeginMembershipPaymentSubscriptionRequestDto,
      paymentMethodRecordFinalization?: PaymentMethodRecordFinalization,
    ): {
      activationRequest: BeginMembershipPaymentSubscriptionRequestDto
      paymentMethod: RetainedPaymentMethod
      paymentMethodRecordFinalization?: PaymentMethodRecordFinalization
    } => {
      if (stateRef.current.recoveryError) throw stateRef.current.recoveryError
      const retainedPaymentMethod = stateRef.current.attempt.retainedPaymentMethod ?? paymentMethod
      const retainedActivationRequest = stateRef.current.attempt.activationRequest ?? activationRequest
      const retainedPaymentMethodRecordFinalization =
        stateRef.current.attempt.paymentMethodRecordFinalization ?? paymentMethodRecordFinalization
      const nextAttempt = attemptSchema.parse({
        ...stateRef.current.attempt,
        activationRequest: retainedActivationRequest,
        retainedPaymentMethod,
        paymentMethodRecordFinalization: retainedPaymentMethodRecordFinalization,
      })
      try {
        window.sessionStorage.setItem(storageKey(maintenancePlanGuid), JSON.stringify(nextAttempt))
      } catch (error) {
        const recoveryError = toError(error, 'Subscription activation recovery storage is unavailable')
        const nextState = { attempt: stateRef.current.attempt, recoveryError }
        stateRef.current = nextState
        setState(nextState)
        throw recoveryError
      }
      const nextState = { attempt: nextAttempt }
      stateRef.current = nextState
      setState(nextState)
      return {
        activationRequest: retainedActivationRequest,
        paymentMethod: retainedPaymentMethod,
        paymentMethodRecordFinalization: retainedPaymentMethodRecordFinalization,
      }
    },
    [maintenancePlanGuid],
  )

  const clear = useCallback(() => {
    try {
      if (typeof window !== 'undefined') window.sessionStorage.removeItem(storageKey(maintenancePlanGuid))
      const nextState = { attempt: newAttempt(maintenancePlanGuid) }
      stateRef.current = nextState
      setState(nextState)
    } catch (error) {
      const recoveryError = toError(error, 'Subscription activation recovery storage could not be cleared')
      const nextState = { attempt: stateRef.current.attempt, recoveryError }
      stateRef.current = nextState
      setState(nextState)
    }
  }, [maintenancePlanGuid])

  return { ...state, clear, retainOperation }
}
